import { MfaEnrolmentStep, MfaEnrolmentStepView } from '@today/ui-domain'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { consumeForcedMfaEnrolment } from '@/auth/forced-mfa-enrolment'
import { useMfaEnrolmentCallback } from '@/hooks/useMfaEnrolment'
import { SECURITY_NAMESPACE } from '@/i18n'

/**
 * `/settings/security/mfa/callback` — where the enrolment sign-in returns
 * (TO-63 privileged-mfa REQ-013): QR code + secret, the 6-digit code, then a
 * confirmation and the way back to Settings.
 *
 * Shared by the Settings re-enrolment and the TO-591 forced first-login
 * enrolment. On success, a FORCED enrolment has had its role granted — which
 * revoked the session server-side — so the user is sent to re-login with their
 * token cleared; a Settings re-enrolment keeps its session and the "back to
 * settings" link.
 */
export function MfaEnrolmentCallbackPage() {
	const { t } = useTranslation(SECURITY_NAMESPACE)
	const [params] = useSearchParams()
	const navigate = useNavigate()
	const enrolment = useMfaEnrolmentCallback(params)

	useEffect(() => {
		if (enrolment.step !== MfaEnrolmentStep.DONE) return
		if (!consumeForcedMfaEnrolment()) return
		localStorage.removeItem('accessToken')
		void navigate('/login', { replace: true })
	}, [enrolment.step, navigate])

	return (
		<MfaEnrolmentStepView
			step={enrolment.step}
			qrCodeSrc={enrolment.qrCodeSrc}
			secretCode={enrolment.secretCode}
			code={enrolment.code}
			codeError={enrolment.codeError ? t(`errors.${enrolment.codeError}`) : null}
			error={enrolment.error ? t(`errors.${enrolment.error}`) : null}
			isBusy={enrolment.isBusy}
			onCodeChange={enrolment.setCode}
			onSubmit={() => void enrolment.submit()}
			backHref="/settings"
			labels={{
				title: t('enrolment.title'),
				loading: t('enrolment.loading'),
				scan: t('enrolment.scan'),
				qrAlt: t('enrolment.qrAlt'),
				secretLabel: t('enrolment.secretLabel'),
				codeLabel: t('enrolment.codeLabel'),
				verify: t('enrolment.verify'),
				done: t('enrolment.done'),
				back: t('enrolment.back'),
			}}
		/>
	)
}
